import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { SETTINGS } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { KeyRound, ShieldCheck, MailPlus, Cake, ShieldPlus, LifeBuoy, Copy, RefreshCw, BarChart3, MessageSquare, Palette, Sparkles, Plus, Trash2, Pencil, Home, RotateCw, CalendarClock, Send, Download, Users, UserCog } from "lucide-react";
import { PAGE_KEYS, PAGE_LABELS, THEME_OPTIONS, DEFAULT_THEMES, getThemesOnce, saveThemes } from "@/hooks/useSectionTheme";
import SenderDomainCard from "@/components/SenderDomainCard";
import HostawayCard from "@/components/HostawayCard";
import TwilioCard from "@/components/TwilioCard";
import SlackCard from "@/components/SlackCard";
import ChannelManagerCard from "@/components/ChannelManagerCard";
import CleaningCompaniesCard from "@/components/CleaningCompaniesCard";
import RateLimitWeeklyCard from "@/components/RateLimitWeeklyCard";
import UiPrefsCard from "@/components/UiPrefsCard";
import PendingWhatsAppCard from "@/components/PendingWhatsAppCard";
import { RequiredGuidesCard } from "@/components/CleanerGuideGate";
import { QuizEditorCard, CheckoutPhotoSettingCard } from "@/components/QuizEditor";
import { DepositBoostCard, CareTipsCard } from "@/components/DamageInvoice";
import { RestartTourCard } from "@/components/AppTour";
import { SyncedChip } from "@/lib/synced";
import { toast } from "sonner";

function SectionThemePickerCard() {
  const [themes, setThemes] = useState({ ...DEFAULT_THEMES });
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    getThemesOnce().then((t) => {
      setThemes({ ...DEFAULT_THEMES, ...t });
      setLoaded(true);
    });
  }, []);

  const pickTheme = (page, theme) => setThemes((prev) => ({ ...prev, [page]: theme }));

  const save = async () => {
    setBusy(true);
    try {
      await saveThemes(themes);
      toast.success("Section themes saved");
    } catch (e) {
      toast.error(apiError(e, "Could not save themes"));
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    setBusy(true);
    try {
      await saveThemes({ ...DEFAULT_THEMES });
      setThemes({ ...DEFAULT_THEMES });
      toast.success("Back to defaults");
    } catch (e) {
      toast.error(apiError(e, "Could not reset themes"));
    } finally {
      setBusy(false);
    }
  };

  const swatchBg = {
    teal:   "bg-gradient-to-br from-teal-100 to-teal-50 border-teal-300",
    indigo: "bg-gradient-to-br from-indigo-100 to-indigo-50 border-indigo-300",
    amber:  "bg-gradient-to-br from-amber-100 to-amber-50 border-amber-300",
    plum:   "bg-gradient-to-br from-violet-100 to-violet-50 border-violet-300",
    stone:  "bg-gradient-to-br from-stone-100 to-stone-50 border-stone-300",
  };

  return (
    <div className="ns-card p-8" data-testid="settings-section-themes-card">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-indigo-50 text-indigo-700 flex items-center justify-center">
          <Palette className="w-5 h-5" />
        </div>
        <div>
          <div className="ns-label">Personalisation</div>
          <div className="font-serif text-2xl">Section theme picker</div>
        </div>
      </div>
      <div className="ns-hairline my-6" />
      <p className="text-sm text-slate-500 max-w-2xl">
        Pick which colour sits on which page&apos;s primary section. Each operator keeps their own
        mapping — your preference follows you across browsers but doesn&apos;t touch anyone else&apos;s view.
      </p>

      <div className="mt-6 grid gap-3" data-testid="settings-section-themes-grid">
        {PAGE_KEYS.map((page) => (
          <div
            key={page}
            data-testid={`settings-section-theme-row-${page}`}
            className="flex items-center justify-between gap-4 py-2 border-b border-slate-100 last:border-0"
          >
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-700">{PAGE_LABELS[page]}</div>
              <div className="text-[11px] text-slate-400">
                Default: {DEFAULT_THEMES[page]}
              </div>
            </div>
            <div className="flex gap-1.5 flex-wrap justify-end">
              {THEME_OPTIONS.map((theme) => {
                const active = themes[page] === theme;
                return (
                  <button
                    key={theme}
                    type="button"
                    data-testid={`settings-section-theme-${page}-${theme}`}
                    data-active={active ? "1" : "0"}
                    onClick={() => pickTheme(page, theme)}
                    disabled={busy || !loaded}
                    title={`${PAGE_LABELS[page]} → ${theme}`}
                    className={`w-9 h-9 rounded-lg border-2 transition ${swatchBg[theme]} ${
                      active
                        ? "ring-2 ring-offset-2 ring-slate-900 scale-105"
                        : "opacity-70 hover:opacity-100"
                    }`}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 flex gap-3">
        <Button
          data-testid="settings-section-themes-save"
          onClick={save}
          disabled={busy || !loaded}
          className="rounded-full ns-btn-primary h-10 px-6"
        >
          {busy ? "Saving…" : "Save my themes"}
        </Button>
        <Button
          data-testid="settings-section-themes-reset"
          variant="outline"
          onClick={reset}
          disabled={busy || !loaded}
          className="rounded-full h-10 px-6"
        >
          Reset to defaults
        </Button>
      </div>
    </div>
  );
}

function SharedOutlookCalendarsCard() {
  // Session Z80 — manage shared mailboxes / resource calendars so cover
  // staff get the same auto-snooze as the owner.
  const [status, setStatus] = useState({ connected: false, emails: [] });
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/oauth/outlook/shared-calendars");
      setStatus(data || { connected: false, emails: [] });
    } catch {
      /* silent — card simply shows "connect Outlook first" */
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => { load(); }, []);

  const persist = async (emails) => {
    setBusy(true);
    try {
      const { data } = await api.put("/oauth/outlook/shared-calendars", { emails });
      setStatus((s) => ({ ...s, emails: data.emails || [] }));
    } catch (err) {
      toast.error(apiError(err, "Could not save shared calendars"));
    } finally {
      setBusy(false);
    }
  };

  const add = () => {
    const clean = draft.trim().toLowerCase();
    if (!clean) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(clean)) {
      toast.error("Enter a valid email address");
      return;
    }
    if (status.emails.includes(clean)) {
      toast.message("Already added");
      setDraft("");
      return;
    }
    const next = [...status.emails, clean];
    setDraft("");
    persist(next);
  };

  const remove = (email) => persist(status.emails.filter((e) => e !== email));

  return (
    <div className="ns-card p-8" data-testid="settings-shared-outlook-card">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-sky-50 text-sky-700 flex items-center justify-center">
          <Users className="w-5 h-5" />
        </div>
        <div>
          <div className="ns-label">Shared calendar mode</div>
          <div className="font-serif text-2xl">Outlook shared mailboxes</div>
        </div>
      </div>
      <div className="ns-hairline my-6" />
      <p className="text-sm text-slate-500 max-w-2xl">
        Add a shared mailbox or resource calendar (e.g. <code>frontdesk@newportstays.co.uk</code>)
        and the auto-snooze probe will also check it on every poll — perfect for cover staff
        covering someone else&apos;s meeting load. Needs the mailbox to have granted you <b>Reviewer</b> or higher via Exchange.
      </p>

      {!loaded ? (
        <div className="mt-6 text-sm text-slate-400">Loading…</div>
      ) : !status.connected ? (
        <div className="mt-6 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          Connect your Outlook Calendar first (Inbox → snooze menu → Connect Outlook Calendar), then come back.
        </div>
      ) : (
        <>
          <div className="mt-6 flex gap-2" data-testid="settings-shared-outlook-form">
            <Input
              data-testid="settings-shared-outlook-input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && add()}
              placeholder="shared@newportstays.co.uk"
              disabled={busy}
              className="rounded-full"
            />
            <Button
              data-testid="settings-shared-outlook-add"
              onClick={add}
              disabled={busy}
              className="rounded-full ns-btn-primary h-10 px-6"
            >
              Add
            </Button>
          </div>

          <div className="mt-5 space-y-2" data-testid="settings-shared-outlook-list">
            {status.emails.length === 0 ? (
              <div className="text-sm text-slate-400">
                No shared calendars yet — the probe only checks your personal Outlook.
              </div>
            ) : (
              status.emails.map((email) => (
                <div
                  key={email}
                  data-testid={`settings-shared-outlook-row-${email}`}
                  className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl border border-slate-200 bg-stone-50"
                >
                  <div className="flex items-center gap-2 text-sm text-slate-700 truncate">
                    <CalendarClock className="w-4 h-4 text-sky-600" />
                    <span className="truncate">{email}</span>
                  </div>
                  <button
                    type="button"
                    data-testid={`settings-shared-outlook-remove-${email}`}
                    onClick={() => remove(email)}
                    disabled={busy}
                    className="text-xs text-slate-400 hover:text-rose-600"
                  >
                    Remove
                  </button>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}

function TwoFactorCard() {
  const { user, setUser } = useAuth();
  const [enabled, setEnabled] = useState(!!user?.two_factor_enabled);
  const [busy, setBusy] = useState(false);
  const forced = user?.role === "owner";

  useEffect(() => {
    setEnabled(!!user?.two_factor_enabled || forced);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.two_factor_enabled, forced]);

  const toggle = async (next) => {
    if (forced && !next) {
      toast.error("Two-factor sign-in is always required for owners");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.put("/auth/two-factor", { enabled: next });
      setEnabled(data.two_factor_enabled);
      setUser({ ...user, two_factor_enabled: data.two_factor_enabled });
      toast.success(
        data.two_factor_enabled
          ? "Two-factor sign-in enabled"
          : "Two-factor sign-in disabled",
      );
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ns-card p-8">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-teal-50 text-teal-700 flex items-center justify-center">
          <ShieldPlus className="w-5 h-5" />
        </div>
        <div>
          <div className="ns-label">Security</div>
          <div className="font-serif text-2xl">Two-factor sign-in</div>
        </div>
      </div>
      <div className="ns-hairline my-6" />
      <p className="text-sm text-slate-500 max-w-xl">
        Every sign-in requires a 6-digit code emailed to your inbox. It keeps
        Newport Stays safe if your password ever leaks.
        {forced && (
          <span className="block mt-2 text-teal-800">
            Always required for owners — this cannot be turned off.
          </span>
        )}
      </p>
      <label className="flex items-center gap-3 text-sm text-slate-700 select-none cursor-pointer mt-4">
        <input
          data-testid={SETTINGS.twoFactorToggle}
          type="checkbox"
          checked={enabled}
          disabled={busy || forced}
          onChange={(e) => toggle(e.target.checked)}
          className="w-4 h-4 accent-teal-600"
        />
        <span>
          {forced
            ? "Two-factor sign-in — always on"
            : "Require a code by email at every sign-in"}
        </span>
      </label>
    </div>
  );
}

function BackupCodesCard() {
  const [remaining, setRemaining] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [freshCodes, setFreshCodes] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    api
      .get("/auth/backup-codes")
      .then((r) => {
        setRemaining(r.data.remaining);
        setGeneratedAt(r.data.generated_at);
      })
      .catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const generate = async () => {
    if (remaining > 0 && !window.confirm("Generate a fresh set? Your existing backup codes will stop working.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/backup-codes/regenerate");
      setFreshCodes(data.codes);
      setRemaining(data.codes.length);
      setGeneratedAt(data.generated_at);
      toast.success("5 backup codes generated — save them somewhere safe");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const copyAll = () => {
    if (!freshCodes) return;
    navigator.clipboard.writeText(freshCodes.join("\n"));
    toast.success("Codes copied to clipboard");
  };

  return (
    <div className="ns-card p-8">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-teal-50 text-teal-700 flex items-center justify-center">
          <LifeBuoy className="w-5 h-5" />
        </div>
        <div>
          <div className="ns-label">Security</div>
          <div className="font-serif text-2xl">Backup codes</div>
        </div>
      </div>
      <div className="ns-hairline my-6" />
      <p className="text-sm text-slate-500 max-w-xl">
        If you're ever locked out of email, use one of these one-time backup codes on the sign-in code screen
        to get in. Each code works once.
      </p>

      {freshCodes ? (
        <div className="mt-5 space-y-3">
          <div className="grid grid-cols-2 gap-2 max-w-md">
            {freshCodes.map((c) => (
              <code
                key={c}
                data-testid="settings-backup-code"
                className="bg-teal-50 border border-teal-100 rounded-lg px-3 py-2 text-center font-mono tracking-wider text-teal-900"
              >
                {c}
              </code>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="settings-backup-copy"
              onClick={copyAll}
              className="inline-flex items-center gap-1.5 text-xs text-teal-700 hover:underline"
            >
              <Copy className="w-3.5 h-3.5" /> Copy all
            </button>
            <button
              onClick={() => setFreshCodes(null)}
              className="text-xs text-slate-400 hover:text-slate-600"
            >
              I've saved them — hide
            </button>
          </div>
          <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-3 max-w-md">
            These codes will only ever be shown once. Print or store them in your password manager now.
          </div>
        </div>
      ) : (
        <div className="mt-5 flex items-center gap-4 flex-wrap">
          <div className="text-sm">
            {remaining === null ? (
              <span className="text-slate-400">Loading…</span>
            ) : remaining > 0 ? (
              <>
                <span className="font-medium text-teal-800">{remaining} of 5</span>{" "}
                <span className="text-slate-500">backup codes remaining</span>
                {generatedAt && (
                  <span className="block text-[11px] text-slate-400">
                    Generated {new Date(generatedAt).toLocaleDateString()}
                  </span>
                )}
              </>
            ) : (
              <span className="text-slate-500">No backup codes yet — generate a fresh set below.</span>
            )}
          </div>
          <Button
            data-testid="settings-backup-generate"
            onClick={generate}
            disabled={busy}
            variant="outline"
            className="rounded-full h-10 px-5"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${busy ? "animate-spin" : ""}`} />
            {remaining > 0 ? "Regenerate 5 codes" : "Generate 5 codes"}
          </Button>
        </div>
      )}
    </div>
  );
}

function ChannelManagerCardSlot() { return null; }

const DAYS_OF_WEEK = [
  "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday",
];

function DigestSparkline({ series }) {
  const [hover, setHover] = useState(null); // { i, x, y }
  if (!series || series.length < 2) return null;
  const width = 320;
  const height = 56;
  const pad = 4;
  const bookings = series.map((s) => Number(s.bookings) || 0);
  const revenue = series.map((s) => Number(s.revenue) || 0);
  const maxB = Math.max(1, ...bookings);
  const maxR = Math.max(1, ...revenue);
  const stepX = (width - pad * 2) / Math.max(1, series.length - 1);
  const pathFor = (arr, max) =>
    arr
      .map((v, i) => {
        const x = pad + i * stepX;
        const y = height - pad - ((v / max) * (height - pad * 2));
        return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(" ");
  const bookingsPath = pathFor(bookings, maxB);
  const revenuePath = pathFor(revenue, maxR);
  const lastPoint = series[series.length - 1];
  const firstPoint = series[0];
  return (
    <div className="mt-3 rounded-xl border border-slate-100 bg-white p-3 relative" data-testid="digest-sparkline">
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Last {series.length} weeks</span>
        <span className="text-[11px] text-slate-400">
          {firstPoint.week_start} → {lastPoint.week_end}
        </span>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-14" preserveAspectRatio="none">
        <path d={revenuePath} fill="none" stroke="#f59e0b" strokeWidth="1.5" opacity="0.55" strokeLinejoin="round" strokeLinecap="round" data-testid="digest-sparkline-revenue" />
        <path d={bookingsPath} fill="none" stroke="#0d7a75" strokeWidth="1.75" strokeLinejoin="round" strokeLinecap="round" data-testid="digest-sparkline-bookings" />
        {series.map((s, i) => {
          const x = pad + i * stepX;
          const y = height - pad - (((Number(s.bookings) || 0) / maxB) * (height - pad * 2));
          return (
            <g key={i}>
              <circle cx={x} cy={y} r="1.6" fill="#0d7a75" />
              <circle
                cx={x}
                cy={y}
                r="8"
                fill="transparent"
                data-testid={`digest-sparkline-hit-${i}`}
                onMouseEnter={() => setHover({ i, xPct: (x / width) * 100, yPct: (y / height) * 100 })}
                onMouseLeave={() => setHover(null)}
                style={{ cursor: "help" }}
              >
                <title>{`${s.week_start} → ${s.week_end}\n${s.bookings} bookings · £${(s.revenue || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`}</title>
              </circle>
            </g>
          );
        })}
      </svg>
      {hover && series[hover.i] && (
        <div
          data-testid="digest-sparkline-tooltip"
          className="absolute pointer-events-none rounded-lg bg-slate-900 text-white text-[10px] px-2.5 py-1.5 shadow-lg -translate-x-1/2 -translate-y-full"
          style={{ left: `${hover.xPct}%`, top: `calc(${hover.yPct}% + 32px)` }}
        >
          <div className="opacity-70 uppercase tracking-[0.12em] text-[9px]">
            {series[hover.i].week_start} → {series[hover.i].week_end}
          </div>
          <div className="mt-0.5 font-medium">
            {series[hover.i].bookings} bookings · £{(series[hover.i].revenue || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </div>
        </div>
      )}
      <div className="mt-1 flex items-center gap-3 text-[10px] text-slate-500">
        <span className="inline-flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-teal-700" /> Bookings
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-amber-500 opacity-70" /> Revenue
        </span>
      </div>
    </div>
  );
}

function DeltaChip({ testid, label, current, previous, prefix = "", betterIsUp = true, float: isFloat = false }) {
  const diff = (Number(current) || 0) - (Number(previous) || 0);
  if (Math.abs(diff) < (isFloat ? 0.005 : 0.5)) {
    return (
      <span
        data-testid={testid}
        className="inline-flex items-center gap-1 px-2 h-6 rounded-full text-[10px] uppercase tracking-[0.08em] bg-slate-100 text-slate-500"
      >
        → same {label}
      </span>
    );
  }
  const up = diff > 0;
  const good = betterIsUp ? up : !up;
  const arrow = up ? "↑" : "↓";
  const absVal = Math.abs(diff);
  const prev = Math.abs(Number(previous) || 0);
  const pct = prev < 0.5 ? Infinity : (absVal / prev) * 100;
  const bigSwing = pct >= 25;
  const shown = isFloat
    ? `${prefix}${absVal.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
    : `${prefix}${Math.round(absVal)}`;
  return (
    <span
      data-testid={testid}
      data-big-swing={bigSwing || undefined}
      title={bigSwing ? `Big swing — ${pct === Infinity ? "up from zero" : pct.toFixed(0) + "%"} vs last week` : undefined}
      className={
        "inline-flex items-center gap-1 px-2 h-6 rounded-full text-[10px] uppercase tracking-[0.08em] " +
        (bigSwing ? "ring-2 ring-offset-1 ring-offset-white animate-pulse " : "") +
        (good
          ? "bg-emerald-50 text-emerald-700" + (bigSwing ? " ring-emerald-300" : "")
          : "bg-rose-50 text-rose-700" + (bigSwing ? " ring-rose-300" : ""))
      }
    >
      {arrow} {shown} {label}
    </span>
  );
}

function WeeklyDigestCard() {
  const [enabled, setEnabled] = useState(true);
  const [dayOfWeek, setDayOfWeek] = useState(0);
  const [hourUtc, setHourUtc] = useState(8);
  const [lastRun, setLastRun] = useState(null);
  const [lastStats, setLastStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testingRecipients, setTestingRecipients] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null); // { html, subject, stats }
  const [eligible, setEligible] = useState([]);
  const [recipientIds, setRecipientIds] = useState(null); // null = fallback default (all owners)
  const [recipientFilter, setRecipientFilter] = useState("");
  const [history, setHistory] = useState([]);
  const [historyEntry, setHistoryEntry] = useState(null); // full HTML view
  const [trend, setTrend] = useState([]);
  const [syncedAt, setSyncedAt] = useState(null);

  const load = () => {
    api
      .get("/settings/weekly-digest")
      .then((r) => {
        setEnabled(r.data.enabled !== false);
        setDayOfWeek(r.data.day_of_week ?? 0);
        setHourUtc(r.data.hour_utc ?? 8);
        setLastRun(r.data.last_run_at || null);
        setLastStats(r.data.last_run_stats || null);
        setRecipientIds(r.data.recipient_user_ids ?? null);
        setSyncedAt(new Date());
      })
      .catch(() => {});
    api
      .get("/settings/weekly-digest/eligible-recipients")
      .then((r) => setEligible(r.data || []))
      .catch(() => setEligible([]));
    api
      .get("/settings/weekly-digest/history")
      .then((r) => setHistory(r.data || []))
      .catch(() => setHistory([]));
    api
      .get("/settings/weekly-digest/trend", { params: { weeks: 8 } })
      .then((r) => setTrend(r.data?.series || []))
      .catch(() => setTrend([]));
  };

  useEffect(load, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings/weekly-digest", {
        enabled,
        day_of_week: dayOfWeek,
        hour_utc: hourUtc,
        recipient_user_ids: recipientIds,
      });
      toast.success("Weekly digest schedule saved");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/settings/weekly-digest/test");
      toast.success(`Test digest sent · ${data.stats?.new_bookings ?? 0} bookings in the last week`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setTesting(false);
    }
  };

  const sendToRecipients = async () => {
    setTestingRecipients(true);
    try {
      const { data } = await api.post("/settings/weekly-digest/test-recipients");
      toast.success(`Fired to ${data.sent}/${data.recipients} recipient(s)`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setTestingRecipients(false);
    }
  };

  const openPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.get("/settings/weekly-digest/preview");
      setPreview(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setPreviewing(false);
    }
  };

  const toggleRecipient = (userId) => {
    const curr = Array.isArray(recipientIds) ? recipientIds : [];
    if (curr.includes(userId)) {
      const next = curr.filter((i) => i !== userId);
      setRecipientIds(next);
    } else {
      setRecipientIds([...curr, userId]);
    }
  };

  const filteredEligible = React.useMemo(() => {
    const q = recipientFilter.trim().toLowerCase();
    if (!q) return eligible;
    return eligible.filter(
      (u) =>
        (u.name || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q)
    );
  }, [eligible, recipientFilter]);

  // Bulk-toggle helpers — when a filter is active, operate ONLY on the filtered
  // subset and MERGE the result with any existing selection so users can build
  // up their picker one filter at a time.
  const bulkApply = (predicate) => {
    const curr = Array.isArray(recipientIds) ? recipientIds : [];
    const source = recipientFilter.trim() ? filteredEligible : eligible;
    const matches = source.filter(predicate).map((u) => u.id);
    if (recipientFilter.trim()) {
      // Merge — respect the search box context
      const merged = Array.from(new Set([...curr, ...matches]));
      setRecipientIds(merged);
    } else {
      // No filter — legacy behaviour: replace the selection wholesale
      setRecipientIds(matches);
    }
  };

  // Live "next send" chip — compute the next UTC moment matching {dayOfWeek, hourUtc}.
  const nextSendPreview = React.useMemo(() => {
    if (!enabled) return "Pauses — nothing scheduled";
    const now = new Date();
    const nowUtcDayIdx = (now.getUTCDay() + 6) % 7; // shift so Mon=0
    let daysAhead = (dayOfWeek - nowUtcDayIdx + 7) % 7;
    // If it's today but the hour has already passed, roll a week forward
    if (daysAhead === 0 && now.getUTCHours() >= hourUtc) {
      daysAhead = 7;
    }
    const next = new Date(Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate() + daysAhead,
      hourUtc, 0, 0, 0,
    ));
    return next.toLocaleString(undefined, {
      weekday: "long", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  }, [enabled, dayOfWeek, hourUtc]);

  const recipientLabel = Array.isArray(recipientIds)
    ? `${recipientIds.length} chosen recipient${recipientIds.length === 1 ? "" : "s"}`
    : "Every owner (default)";

  return (
    <div className="ns-card p-8" data-testid="settings-weekly-digest-card">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-teal-50 text-teal-700 flex items-center justify-center">
          <CalendarClock className="w-5 h-5" />
        </div>
        <div>
          <div className="ns-label">Automations</div>
          <div className="font-serif text-2xl">Weekly digest</div>
        </div>
      </div>
      <div className="ns-hairline my-6" />
      <p className="text-sm text-slate-500 max-w-xl">
        A single email rolling up the last 7 days — new bookings, refunds, cleaning
        turnover and guest journey completion. Pick the day and hour that suits
        your inbox.
      </p>

      <div className="mt-5 grid gap-4 md:grid-cols-3 max-w-3xl">
        <label className="block">
          <span className="ns-label">Day of week (UTC)</span>
          <select
            data-testid="digest-day-select"
            className="mt-2 h-11 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
            value={dayOfWeek}
            onChange={(e) => setDayOfWeek(Number(e.target.value))}
          >
            {DAYS_OF_WEEK.map((d, i) => (
              <option key={d} value={i}>{d}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="ns-label">Hour (UTC, 24h)</span>
          <select
            data-testid="digest-hour-select"
            className="mt-2 h-11 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
            value={hourUtc}
            onChange={(e) => setHourUtc(Number(e.target.value))}
          >
            {Array.from({ length: 24 }).map((_, h) => (
              <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>
            ))}
          </select>
        </label>
        <div className="flex items-end">
          <label className="flex items-center gap-3 text-sm text-slate-700 select-none cursor-pointer pb-2">
            <input
              data-testid="digest-enabled-toggle"
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="w-4 h-4 accent-teal-600"
            />
            <span>Enabled</span>
          </label>
        </div>
      </div>

      {/* Live "next send" preview — updates as the owner tweaks the dropdowns */}
      <div
        className="mt-4 inline-flex items-center gap-2 rounded-full bg-teal-50 border border-teal-100 pl-3 pr-4 py-1.5 text-xs text-teal-900"
        data-testid="digest-next-send"
      >
        <CalendarClock className="w-3.5 h-3.5" />
        <span className="text-[10px] uppercase tracking-[0.16em] text-teal-700">Next send</span>
        <span className="font-medium">{nextSendPreview}</span>
      </div>

      {/* Recipient picker */}
      <div className="mt-6 max-w-3xl" data-testid="digest-recipient-picker">
        <div className="flex items-baseline justify-between mb-2">
          <span className="ns-label">Recipients</span>
          <span className="text-[11px] text-slate-500">{recipientLabel}</span>
        </div>
        <div className="rounded-xl border border-slate-100 divide-y divide-slate-100 bg-white">
          <label className="flex items-center gap-3 px-4 py-3 cursor-pointer select-none">
            <input
              data-testid="digest-recipient-default"
              type="radio"
              name="digest-recipient-mode"
              checked={recipientIds === null}
              onChange={() => setRecipientIds(null)}
              className="accent-teal-600"
            />
            <div>
              <div className="text-sm text-slate-800">Every owner (default)</div>
              <div className="text-[11px] text-slate-500">Falls back to any user marked <code>receives_digest</code></div>
            </div>
          </label>
          <label className="flex items-start gap-3 px-4 py-3 cursor-pointer select-none">
            <input
              data-testid="digest-recipient-custom"
              type="radio"
              name="digest-recipient-mode"
              checked={Array.isArray(recipientIds)}
              onChange={() => setRecipientIds(Array.isArray(recipientIds) ? recipientIds : [])}
              className="accent-teal-600 mt-1"
            />
            <div className="flex-1">
              <div className="text-sm text-slate-800">Choose specific admins &amp; owners</div>
              {Array.isArray(recipientIds) && (
                <div className="mt-3 space-y-3" data-testid="digest-recipient-chips">
                  {/* Bulk-toggle quick chips */}
                  {eligible.length > 0 && (
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[10px] uppercase tracking-[0.16em] text-slate-500 mr-1">Quick pick</span>
                      <button
                        type="button"
                        data-testid="digest-bulk-owners"
                        onClick={() => bulkApply((u) => u.role === "owner")}
                        className="inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full text-[11px] bg-white border border-slate-200 text-slate-700 hover:border-teal-300 hover:text-teal-700"
                      >
                        <UserCog className="w-3 h-3" /> Only owners
                      </button>
                      <button
                        type="button"
                        data-testid="digest-bulk-admins"
                        onClick={() => bulkApply((u) => u.role === "admin")}
                        className="inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full text-[11px] bg-white border border-slate-200 text-slate-700 hover:border-teal-300 hover:text-teal-700"
                      >
                        <Users className="w-3 h-3" /> Only admins
                      </button>
                      <button
                        type="button"
                        data-testid="digest-bulk-all"
                        onClick={() => bulkApply(() => true)}
                        className="inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full text-[11px] bg-white border border-slate-200 text-slate-700 hover:border-teal-300 hover:text-teal-700"
                      >
                        Select all ({recipientFilter.trim() ? filteredEligible.length : eligible.length})
                      </button>
                      <button
                        type="button"
                        data-testid="digest-bulk-clear"
                        onClick={() => setRecipientIds([])}
                        className="inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full text-[11px] bg-white border border-slate-200 text-slate-500 hover:border-slate-300"
                      >
                        Clear
                      </button>
                    </div>
                  )}
                  {eligible.length > 3 && (
                    <input
                      data-testid="digest-recipient-filter"
                      type="text"
                      placeholder={`Filter ${eligible.length} people by name or email…`}
                      value={recipientFilter}
                      onChange={(e) => setRecipientFilter(e.target.value)}
                      className="h-9 w-full rounded-full border border-slate-200 bg-white px-4 text-xs placeholder:text-slate-400 focus:outline-none focus:border-teal-400"
                    />
                  )}
                  <div className="flex flex-wrap gap-2">
                  {eligible.length === 0 && (
                    <span className="text-[11px] text-slate-400 italic">No admin or owner accounts loaded.</span>
                  )}
                  {eligible
                    .filter((u) => {
                      if (!recipientFilter.trim()) return true;
                      const q = recipientFilter.trim().toLowerCase();
                      return (
                        (u.name || "").toLowerCase().includes(q) ||
                        (u.email || "").toLowerCase().includes(q)
                      );
                    })
                    .map((u) => {
                    const on = recipientIds.includes(u.id);
                    return (
                      <button
                        key={u.id}
                        type="button"
                        data-testid={`digest-recipient-chip-${u.id}`}
                        onClick={() => toggleRecipient(u.id)}
                        className={
                          "inline-flex items-center gap-2 px-3 h-8 rounded-full text-xs border transition " +
                          (on
                            ? "bg-teal-600 border-teal-600 text-white"
                            : "bg-white border-slate-200 text-slate-700 hover:border-teal-300 hover:text-teal-700")
                        }
                      >
                        <span
                          className={
                            "w-4 h-4 rounded-full flex items-center justify-center text-[10px] " +
                            (on ? "bg-white/25 text-white" : "bg-slate-100 text-slate-500")
                          }
                        >
                          {u.role?.[0]?.toUpperCase() || "?"}
                        </span>
                        <span>{u.name || u.email}</span>
                      </button>
                    );
                  })}
                  </div>
                </div>
              )}
            </div>
          </label>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <Button
          data-testid="digest-save-btn"
          type="button"
          disabled={busy}
          onClick={save}
          className="rounded-full ns-btn-primary h-10 px-5"
        >
          {busy ? "Saving…" : "Save schedule"}
        </Button>
        <Button
          data-testid="digest-preview-btn"
          type="button"
          variant="outline"
          disabled={previewing}
          onClick={openPreview}
          className="rounded-full h-10 px-5 gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${previewing ? "animate-spin" : ""}`} />
          {previewing ? "Building…" : "Preview digest email"}
        </Button>
        <Button
          data-testid="digest-test-btn"
          type="button"
          variant="outline"
          disabled={testing}
          onClick={sendTest}
          className="rounded-full h-10 px-5 gap-2"
        >
          <Send className="w-4 h-4" />
          {testing ? "Sending…" : "Send test to me"}
        </Button>
        {Array.isArray(recipientIds) && recipientIds.length > 0 && (
          <Button
            data-testid="digest-test-recipients-btn"
            type="button"
            variant="outline"
            disabled={testingRecipients}
            onClick={sendToRecipients}
            className="rounded-full h-10 px-5 gap-2 text-teal-700 border-teal-200 hover:bg-teal-50"
          >
            <Send className="w-4 h-4" />
            {testingRecipients ? "Firing…" : `Send test to selected (${recipientIds.length})`}
          </Button>
        )}
      </div>

      {lastRun && (
        <div className="mt-6 rounded-lg bg-stone-50 p-4 text-xs text-slate-600" data-testid="digest-last-run">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[0.16em] text-teal-700 flex items-center gap-2 flex-wrap">
                <span>Last run</span>
                <SyncedChip syncedAt={syncedAt} testid="digest-synced-chip" onRefresh={load} />
              </div>
              <div className="mt-1 font-serif text-sm text-slate-800">{new Date(lastRun).toLocaleString()}</div>
            </div>
            {history.length > 0 && (
              <div className="flex items-center gap-1.5 flex-wrap" data-testid="digest-history-chips">
                <span className="text-[10px] uppercase tracking-[0.16em] text-slate-500 mr-1">Archive</span>
                {history.map((h) => (
                  <button
                    key={h.id}
                    type="button"
                    data-testid={`digest-history-chip-${h.id}`}
                    onClick={async () => {
                      try {
                        const { data } = await api.get(`/settings/weekly-digest/history/${h.id}`);
                        setHistoryEntry(data);
                      } catch (err) {
                        toast.error(apiError(err));
                      }
                    }}
                    className="inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full text-[11px] bg-white border border-slate-200 text-slate-700 hover:border-teal-300 hover:text-teal-700"
                    title={h.subject}
                  >
                    {new Date(h.sent_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                  </button>
                ))}              </div>
            )}
          </div>
          {lastStats && (
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
              <span>{lastStats.new_bookings ?? 0} bookings</span>
              <span>£{(lastStats.total_revenue ?? 0).toLocaleString()} gross</span>
              <span>{lastStats.refunds_issued ?? 0} refund(s)</span>
              <span>{lastStats.cleaning_done ?? 0} turnovers done</span>
            </div>
          )}
          {lastStats?.previous && (
            <div className="mt-3 flex flex-wrap gap-1.5" data-testid="digest-week-compare">
              <span className="text-[10px] uppercase tracking-[0.16em] text-slate-500 mr-1 self-center">Vs last week</span>
              <DeltaChip
                testid="digest-delta-bookings"
                label="bookings"
                current={lastStats.new_bookings ?? 0}
                previous={lastStats.previous.new_bookings ?? 0}
              />
              <DeltaChip
                testid="digest-delta-revenue"
                label="revenue"
                current={lastStats.total_revenue ?? 0}
                previous={lastStats.previous.total_revenue ?? 0}
                prefix="£"
                float
              />
              <DeltaChip
                testid="digest-delta-refunds"
                label="refunds"
                current={lastStats.refunds_issued ?? 0}
                previous={lastStats.previous.refunds_issued ?? 0}
                betterIsUp={false}
              />
              <DeltaChip
                testid="digest-delta-turnovers"
                label="turnovers"
                current={lastStats.cleaning_done ?? 0}
                previous={lastStats.previous.cleaning_done ?? 0}
              />
            </div>
          )}
          {trend.length >= 2 && (
            <DigestSparkline series={trend} />
          )}
        </div>
      )}

      <Dialog open={!!historyEntry} onOpenChange={(o) => !o && setHistoryEntry(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" data-testid="digest-history-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Archived digest</DialogTitle>
            <DialogDescription>
              Sent {historyEntry ? new Date(historyEntry.sent_at).toLocaleString() : ""} to {historyEntry?.recipients ?? 0} recipient(s).
              Subject: <code className="text-slate-600">{historyEntry?.subject || ""}</code>
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 -mx-6 px-6">
            <iframe
              data-testid="digest-history-iframe"
              title="Archived digest"
              srcDoc={historyEntry?.html || ""}
              style={{ width: "100%", minHeight: 620, border: "1px solid #e2e8f0", borderRadius: 12, background: "#fdfbf7" }}
            />
          </div>
          <div className="pt-4 flex justify-end gap-3 border-t border-slate-100">
            <Button
              data-testid="digest-history-download-btn"
              variant="outline"
              className="rounded-full h-10 px-5 gap-2"
              onClick={() => {
                if (!historyEntry?.html) return;
                const blob = new Blob([historyEntry.html], { type: "text/html" });
                const url = URL.createObjectURL(blob);
                const date = historyEntry.sent_at
                  ? new Date(historyEntry.sent_at).toISOString().slice(0, 10)
                  : "digest";
                const a = document.createElement("a");
                a.href = url;
                a.download = `newport-stays-digest-${date}.html`;
                a.click();
                URL.revokeObjectURL(url);
                toast.success("HTML digest saved");
              }}
            >
              <Download className="w-4 h-4" />
              Download HTML
            </Button>
            <Button variant="outline" className="rounded-full h-10 px-5" onClick={() => setHistoryEntry(null)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" data-testid="digest-preview-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Weekly digest preview</DialogTitle>
            <DialogDescription>
              This is the exact HTML that would land in inboxes if we dispatched right now.
              Subject line: <code className="text-slate-600">{preview?.subject || ""}</code>
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 -mx-6 px-6">
            <iframe
              data-testid="digest-preview-iframe"
              title="Weekly digest preview"
              srcDoc={preview?.html || ""}
              style={{ width: "100%", minHeight: 620, border: "1px solid #e2e8f0", borderRadius: 12, background: "#fdfbf7" }}
            />
          </div>
          <div className="pt-4 flex justify-end gap-3 border-t border-slate-100">
            <Button variant="outline" className="rounded-full h-10 px-5" onClick={() => setPreview(null)}>
              Close preview
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ChannelManagerCardSlotUnused() { return null; }

function AiDraftLimitsCard() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [countCap, setCountCap] = useState("");
  const [costCap, setCostCap] = useState("");

  useEffect(() => {
    api.get("/inbox/draft-limits").then((r) => {
      setCfg(r.data);
      setCountCap(String(r.data.day_count_cap));
      setCostCap(String(r.data.day_cost_cap_usd));
    }).catch(() => {});
  }, []);

  if (!cfg) return null;
  const overCount = cfg.used_today_count >= cfg.day_count_cap;
  const overCost = cfg.used_today_cost_usd >= cfg.day_cost_cap_usd;
  const pctCount = Math.min(100, (cfg.used_today_count / cfg.day_count_cap) * 100);
  const pctCost = Math.min(100, (cfg.used_today_cost_usd / cfg.day_cost_cap_usd) * 100);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/inbox/draft-limits", {
        day_count_cap: Number(countCap) || cfg.day_count_cap,
        day_cost_cap_usd: Number(costCap) || cfg.day_cost_cap_usd,
      });
      const { data } = await api.get("/inbox/draft-limits");
      setCfg(data);
      setCountCap(String(data.day_count_cap));
      setCostCap(String(data.day_cost_cap_usd));
      toast.success("AI draft limits saved");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="settings-ai-draft-limits" className="ns-card p-8">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-indigo-50 text-indigo-700 flex items-center justify-center">
          <Sparkles className="w-5 h-5" />
        </div>
        <div>
          <div className="ns-label">Inbox AI</div>
          <div className="font-serif text-2xl">Daily draft limits</div>
        </div>
      </div>
      <div className="ns-hairline my-6" />
      <p className="text-sm text-slate-500 max-w-xl mb-4">
        Guardrails to keep an unusual day (or a runaway integration) from surprising your LLM bill.
        Hit either cap and the "AI draft" button politely returns a 429 with the reason — until midnight UTC.
      </p>
      <div className="grid md:grid-cols-2 gap-4 max-w-xl">
        <div>
          <Label className="ns-label">Drafts per day</Label>
          <Input
            data-testid="ai-limit-count-input"
            type="number" min={1} step={1}
            className="mt-2 h-11 bg-white"
            value={countCap}
            onChange={(e) => setCountCap(e.target.value)}
          />
          <div className={`mt-2 h-1.5 rounded-full ${overCount ? "bg-red-100" : "bg-indigo-100"} overflow-hidden`}>
            <div className={`h-full ${overCount ? "bg-red-500" : "bg-indigo-500"}`} style={{ width: `${pctCount}%` }} />
          </div>
          <div className="text-[11px] text-slate-500 mt-1.5">
            Used today: <span className="font-mono">{cfg.used_today_count}</span> of {cfg.day_count_cap}
          </div>
        </div>
        <div>
          <Label className="ns-label">Spend cap (USD/day)</Label>
          <Input
            data-testid="ai-limit-cost-input"
            type="number" min={0.05} step={0.05}
            className="mt-2 h-11 bg-white"
            value={costCap}
            onChange={(e) => setCostCap(e.target.value)}
          />
          <div className={`mt-2 h-1.5 rounded-full ${overCost ? "bg-red-100" : "bg-indigo-100"} overflow-hidden`}>
            <div className={`h-full ${overCost ? "bg-red-500" : "bg-indigo-500"}`} style={{ width: `${pctCost}%` }} />
          </div>
          <div className="text-[11px] text-slate-500 mt-1.5">
            Used today: <span className="font-mono">${Number(cfg.used_today_cost_usd).toFixed(4)}</span> of ${Number(cfg.day_cost_cap_usd).toFixed(2)}
          </div>
        </div>
      </div>
      <div className="mt-5">
        <Button data-testid="ai-limit-save" onClick={save} disabled={busy} className="rounded-full ns-btn-primary h-11 px-6">
          {busy ? "Saving…" : "Save limits"}
        </Button>
      </div>
    </div>
  );
}

function OtaBridgeCard() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newSecret, setNewSecret] = useState("");
  const webhookUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api/inbox/bridge";

  useEffect(() => {
    api.get("/settings/ota-bridge").then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  if (!cfg) return null;

  const save = async () => {
    setBusy(true);
    try {
      const payload = { enabled: cfg.enabled };
      if (newSecret.trim()) payload.secret = newSecret.trim();
      await api.put("/settings/ota-bridge", payload);
      const { data } = await api.get("/settings/ota-bridge");
      setCfg(data);
      setNewSecret("");
      toast.success("OTA bridge saved");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="settings-ota-bridge" className="ns-card p-8">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-indigo-50 text-indigo-700 flex items-center justify-center">
          <MailPlus className="w-5 h-5" />
        </div>
        <div>
          <div className="ns-label">Unified inbox</div>
          <div className="font-serif text-2xl">OTA message bridge</div>
        </div>
      </div>
      <div className="ns-hairline my-6" />
      <p className="text-sm text-slate-500 max-w-xl mb-4">
        Forward Airbnb, Booking.com and Vrbo guest messages into your Newport Stays inbox — no more tab-toggling.
        Point a Zapier or Make.com hook at the URL below (or a custom bridge) and every incoming OTA message will land
        in the unified stream.
      </p>
      <div className="mb-4 flex flex-wrap gap-2" data-testid="ota-bridge-templates">
        <a
          data-testid="ota-template-airbnb"
          href="https://zapier.com/app/editor/template?utm_source=newportstays&hint=Gmail%20trigger%3A%20from%3A%40airbnb.com%20-%3E%20Webhooks%20POST"
          target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-2 px-3 h-9 rounded-full text-xs bg-white border border-rose-200 text-rose-700 hover:bg-rose-50"
          title="Open Zapier with the Airbnb → Newport Stays template"
        >
          Airbnb template →
        </a>
        <a
          data-testid="ota-template-booking"
          href="https://zapier.com/app/editor/template?utm_source=newportstays&hint=Gmail%20trigger%3A%20from%3A%40booking.com%20-%3E%20Webhooks%20POST"
          target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-2 px-3 h-9 rounded-full text-xs bg-white border border-blue-200 text-blue-700 hover:bg-blue-50"
          title="Open Zapier with the Booking.com → Newport Stays template"
        >
          Booking.com template →
        </a>
        <a
          data-testid="ota-template-vrbo"
          href="https://zapier.com/app/editor/template?utm_source=newportstays&hint=Gmail%20trigger%3A%20from%3A%40vrbo.com%20-%3E%20Webhooks%20POST"
          target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-2 px-3 h-9 rounded-full text-xs bg-white border border-amber-200 text-amber-700 hover:bg-amber-50"
          title="Open Zapier with the Vrbo → Newport Stays template"
        >
          Vrbo template →
        </a>
        <span className="text-[11px] text-slate-400 self-center ml-2">
          Each template pre-fills the trigger (Gmail from the OTA) and target (POST to the webhook below with your secret).
        </span>
      </div>
      <div className="grid gap-4 max-w-2xl">
        <div>
          <Label className="ns-label">Webhook URL</Label>
          <div className="mt-2 flex items-center gap-2">
            <Input readOnly value={webhookUrl} className="h-11 bg-stone-50 font-mono text-xs" />
            <Button
              type="button"
              variant="outline"
              className="rounded-full h-11 px-4 text-xs shrink-0"
              onClick={() => { navigator.clipboard.writeText(webhookUrl); toast.success("Copied"); }}
            >
              Copy
            </Button>
          </div>
          <div className="text-[11px] text-slate-400 mt-1.5">
            POST JSON with headers <code className="text-slate-500">X-Bridge-Secret: &lt;your secret&gt;</code> and body
            <code className="text-slate-500"> {"{ channel, direction, from, to, subject, body, sent_at, contact_email? }"}</code>.
          </div>
        </div>
        <div>
          <Label className="ns-label">Shared secret</Label>
          <Input
            data-testid="ota-bridge-secret-input"
            placeholder={cfg.secret_set ? `••••${cfg.secret_last4}` : "Set a strong random string"}
            className="mt-2 h-11 bg-white font-mono text-xs"
            value={newSecret}
            onChange={(e) => setNewSecret(e.target.value)}
          />
          <div className="text-[11px] text-slate-400 mt-1.5">
            {cfg.secret_set ? "Leave blank to keep the current secret." : "Required — messages without a matching secret are rejected."}
          </div>
        </div>
        <label className="flex items-center gap-3 text-sm text-slate-700 select-none cursor-pointer">
          <input
            data-testid="ota-bridge-enabled"
            type="checkbox"
            checked={!!cfg.enabled}
            onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
            className="w-4 h-4 accent-teal-600"
          />
          <span>Accept incoming OTA messages</span>
        </label>
        <div>
          <Button data-testid="ota-bridge-save" onClick={save} disabled={busy} className="rounded-full ns-btn-primary h-11 px-6">
            {busy ? "Saving…" : "Save bridge"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ConnectAirbnbBanner({ onConnect, loading, count, onScrollToTable, links }) {
  const [stepIdx, setStepIdx] = useState(-1);
  const [running, setRunning] = useState(false);
  const [success, setSuccess] = useState(false);
  const [copiedAll, setCopiedAll] = useState(false);
  const steps = [
    "Verifying Airbnb host account…",
    `Fetching your ${count} listing(s)…`,
    "Minting signed webhook URLs…",
    "Wiring the unified inbox bridge…",
  ];

  const copyAllUrls = async () => {
    if (!Array.isArray(links) || links.length === 0) return;
    const csv =
      "Property,Signed webhook URL\n" +
      links
        .map((l) => `"${(l.property_name || "").replace(/"/g, '""')}",${l.webhook_url}`)
        .join("\n");
    try {
      await navigator.clipboard.writeText(csv);
      setCopiedAll(true);
      toast.success(`Copied ${links.length} URL(s) as CSV`);
      setTimeout(() => setCopiedAll(false), 2500);
    } catch (e) {
      toast.error("Clipboard blocked — grant permission and try again");
    }
  };

  const runFakeOAuth = async () => {
    setRunning(true);
    setSuccess(false);
    for (let i = 0; i < steps.length; i++) {
      setStepIdx(i);
      // eslint-disable-next-line no-await-in-loop
      await new Promise((res) => setTimeout(res, 550));
    }
    setStepIdx(steps.length);
    await onConnect();
    setRunning(false);
    setSuccess(true);
  };

  if (success) {
    return (
      <div
        data-testid="airbnb-connect-success"
        className="mb-6 rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50 via-white to-teal-50 p-6"
      >
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-12 h-12 rounded-2xl bg-white shadow-sm ring-1 ring-emerald-100 flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-emerald-600" />
          </div>
          <div className="flex-1 min-w-[280px]">
            <div className="ns-label text-emerald-700">Airbnb connected 🎉</div>
            <div className="font-serif text-xl text-slate-900 mt-1">
              You&apos;re wired up — {count} fresh URL{count === 1 ? "" : "s"} ready to paste
            </div>
            <ul className="mt-4 space-y-1.5 text-sm" data-testid="airbnb-connect-success-checklist">
              {[
                "Host account verified",
                `${count} listing(s) matched to your Newport Stays inventory`,
                "Signed webhook URLs minted (unique per property)",
                "Inbox bridge live — messages will land tagged to the right unit",
              ].map((line) => (
                <li key={line} className="flex items-center gap-2 text-emerald-800">
                  <span className="w-4 h-4 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-[10px]">
                    ✓
                  </span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
            <div className="mt-4 rounded-lg bg-white border border-emerald-100 px-4 py-3 text-xs text-slate-600">
              <strong className="text-emerald-800">Next:</strong> copy each URL into a Zapier
              &ldquo;Webhooks POST&rdquo; step downstream of a Gmail-from-Airbnb trigger. The
              table below has copy buttons for every listing.
            </div>
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            <Button
              data-testid="airbnb-connect-see-urls-btn"
              type="button"
              onClick={() => {
                setSuccess(false);
                onScrollToTable?.();
              }}
              className="rounded-full h-11 px-6 ns-btn-primary"
            >
              See fresh URLs
            </Button>
            <Button
              data-testid="airbnb-connect-copy-all-btn"
              type="button"
              variant="outline"
              onClick={copyAllUrls}
              disabled={!Array.isArray(links) || links.length === 0}
              className="rounded-full h-10 px-5 gap-2"
            >
              <Copy className="w-4 h-4" />
              {copiedAll ? `Copied ${links?.length || 0}` : `Copy all ${links?.length || count} URLs`}
            </Button>
            <button
              type="button"
              onClick={() => setSuccess(false)}
              className="text-[11px] text-slate-500 hover:text-slate-800"
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="airbnb-connect-banner"
      className="mb-6 rounded-2xl border border-rose-100 bg-gradient-to-br from-rose-50 via-white to-amber-50 p-6"
    >
      <div className="flex items-start gap-4 flex-wrap">
        <div className="w-12 h-12 rounded-2xl bg-white shadow-sm ring-1 ring-rose-100 flex items-center justify-center">
          <Home className="w-6 h-6 text-rose-500" />
        </div>
        <div className="flex-1 min-w-[280px]">
          <div className="ns-label text-rose-700">One-click setup</div>
          <div className="font-serif text-xl text-slate-900 mt-1">
            Connect Airbnb to Newport Stays
          </div>
          <p className="text-sm text-slate-600 mt-2 max-w-xl">
            Get every guest message from your {count} Airbnb listing(s) into the unified
            inbox in seconds. We&apos;ll mint a signed webhook URL per property and
            hand them to you — paste into a Zapier &ldquo;Webhooks POST&rdquo; step
            downstream of a Gmail-from-Airbnb trigger, and messages start flowing.
          </p>

          {stepIdx >= 0 && (
            <ul className="mt-4 space-y-1.5 text-sm" data-testid="airbnb-connect-progress">
              {steps.map((s, i) => (
                <li
                  key={s}
                  className={`flex items-center gap-2 ${i < stepIdx ? "text-teal-700" : i === stepIdx ? "text-slate-900" : "text-slate-400"}`}
                >
                  <span
                    className={`w-4 h-4 rounded-full flex items-center justify-center ${i < stepIdx ? "bg-teal-100 text-teal-700" : i === stepIdx ? "bg-slate-200 text-slate-500" : "bg-slate-100 text-slate-300"}`}
                  >
                    {i < stepIdx ? "✓" : i === stepIdx ? "…" : ""}
                  </span>
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <Button
          data-testid="airbnb-connect-btn"
          type="button"
          onClick={runFakeOAuth}
          disabled={running || loading}
          className="rounded-full h-11 px-6 ns-btn-primary shrink-0"
        >
          {running ? "Connecting…" : "Connect Airbnb"}
        </Button>
      </div>
    </div>
  );
}

function AirbnbLinksCard() {
  const [links, setLinks] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [rotatingId, setRotatingId] = useState(null);
  const [rotatingAll, setRotatingAll] = useState(false);
  const [historyFor, setHistoryFor] = useState(null); // {property_id, property_name}
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const tableRef = React.useRef(null);
  const scrollToTable = () => {
    tableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  useEffect(() => {
    api.get("/settings/airbnb-links").then((r) => setLinks(r.data)).catch(() => setLinks([]));
  }, []);

  if (!links) return null;

  const timeAgo = (iso) => {
    if (!iso) return null;
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (Number.isNaN(s) || s < 0) return null;
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  };

  const copy = (link) => {
    navigator.clipboard.writeText(link.webhook_url);
    setCopiedId(link.property_id);
    toast.success(`Copied ${link.property_name} URL`);
    setTimeout(() => setCopiedId((v) => (v === link.property_id ? null : v)), 2500);
  };

  const rotate = async (link) => {
    if (!window.confirm(
      `Rotate the signed URL for “${link.property_name}”?\n\n` +
      `Any old URL (e.g. in Zapier) will stop working immediately — you'll need to paste the fresh one back into your Zap.`
    )) return;
    setRotatingId(link.property_id);
    try {
      const { data } = await api.post(`/settings/airbnb-links/${link.property_id}/rotate`);
      setLinks((ls) => ls.map((l) =>
        l.property_id === link.property_id
          ? {
              ...l,
              webhook_url: data.webhook_url,
              has_custom_salt: true,
              rotated_at: data.rotated_at,
              rotated_by: data.rotated_by,
            }
          : l,
      ));
      toast.success(`New URL generated for ${link.property_name} — paste it into Zapier`);
    } catch (err) {
      toast.error(apiError(err, "Rotate failed"));
    } finally {
      setRotatingId(null);
    }
  };

  const openHistory = async (link) => {
    setHistoryFor({ property_id: link.property_id, property_name: link.property_name });
    setHistory(null);
    setHistoryLoading(true);
    try {
      const { data } = await api.get(`/settings/airbnb-links/${link.property_id}/history`);
      setHistory(data.rotations || []);
    } catch (err) {
      toast.error(apiError(err, "History fetch failed"));
      setHistoryFor(null);
    } finally {
      setHistoryLoading(false);
    }
  };

  const rotateAll = async () => {
    if (!window.confirm(
      `Rotate ALL ${links.length} Airbnb signed URLs?\n\n` +
      `Every existing URL will die immediately. You'll get an email with the fresh list — paste each one back into Zapier.`
    )) return;
    setRotatingAll(true);
    try {
      const { data } = await api.post(`/settings/airbnb-links/rotate-all`);
      setLinks(data.links || []);
      toast.success(
        `Rotated ${data.rotated} URL${data.rotated === 1 ? "" : "s"} — fresh list emailed to you`
      );
    } catch (err) {
      toast.error(apiError(err, "Bulk rotate failed"));
    } finally {
      setRotatingAll(false);
    }
  };

  const rotatedCount = links.filter((l) => l.has_custom_salt).length;

  return (
    <div data-testid="settings-airbnb-links" className="ns-card p-8">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-rose-50 text-rose-700 flex items-center justify-center">
            <Home className="w-5 h-5" />
          </div>
          <div>
            <div className="ns-label">Unified inbox</div>
            <div className="font-serif text-2xl">Airbnb auto-connect</div>
          </div>
        </div>
        {links.length > 0 && (
          <Button
            data-testid="airbnb-rotate-all-btn"
            type="button"
            variant="outline"
            className="rounded-full h-10 px-5 text-amber-700 border-amber-200 hover:bg-amber-50"
            onClick={rotateAll}
            disabled={rotatingAll}
            title="Regenerate every signed URL and email you the fresh list — perfect after a Zapier hand-off"
          >
            <RotateCw className={`w-4 h-4 mr-2 ${rotatingAll ? "animate-spin" : ""}`} />
            {rotatingAll ? "Rotating…" : `Rotate all${rotatedCount ? ` · ${rotatedCount} rotated` : ""}`}
          </Button>
        )}
      </div>
      <div className="ns-hairline my-6" />
      {rotatedCount === 0 && links.length > 0 && <ConnectAirbnbBanner onConnect={rotateAll} loading={rotatingAll} count={links.length} onScrollToTable={scrollToTable} links={links} />}
      <p className="text-sm text-slate-500 max-w-2xl mb-4">
        One stable signed webhook per listing — no shared secret to leak, no listing mix-ups. Paste the URL for a
        property into Zapier&#39;s <em>Webhooks by Zapier → POST</em> step downstream of a Gmail trigger
        (<code className="text-slate-600">from:@airbnb.com</code>). Body payload:{" "}
        <code className="text-slate-600">{"{ from, body, subject?, sent_at?, contact_email? }"}</code>. Each message
        lands in the unified inbox tagged to the correct property. If a URL ever leaks, hit <strong>Rotate</strong>{" "}
        to revoke the old one instantly — or <strong>Rotate all</strong> after a Zapier account hand-off.
      </p>
      {links.length === 0 ? (
        <div className="text-sm text-slate-400 italic">Add a property first — links generate automatically.</div>
      ) : (
        <div ref={tableRef} className="overflow-x-auto -mx-2" data-testid="airbnb-links-table-wrap">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left">
                <th className="ns-label px-2 py-2">Property</th>
                <th className="ns-label px-2 py-2">Signed webhook URL</th>
                <th className="ns-label px-2 py-2 text-right w-[200px]">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="airbnb-links-tbody">
              {links.map((l) => (
                <tr key={l.property_id} className="border-t border-slate-100">
                  <td className="px-2 py-2 align-top">
                    <div className="font-medium text-slate-900">
                      {l.property_name || "(unnamed)"}
                      {l.has_custom_salt && (
                        <button
                          type="button"
                          data-testid={`airbnb-link-rotated-${l.property_id}`}
                          onClick={() => openHistory(l)}
                          className="ml-2 inline-flex items-center rounded-full bg-amber-50 text-amber-700 px-2 py-0.5 text-[10px] uppercase tracking-wider cursor-pointer hover:bg-amber-100 transition"
                          title={l.rotated_by ? `Rotated by ${l.rotated_by} — click for full history` : "Click to see rotation history"}
                        >
                          Rotated
                        </button>
                      )}
                      {(() => {
                        const days = l.rotated_at
                          ? Math.floor((Date.now() - new Date(l.rotated_at).getTime()) / (1000 * 60 * 60 * 24))
                          : null;
                        const stale = !l.has_custom_salt || (days !== null && days >= 90);
                        if (!stale) return null;
                        return (
                          <span
                            data-testid={`airbnb-link-stale-${l.property_id}`}
                            className="ml-2 inline-flex items-center rounded-full bg-rose-50 text-rose-700 px-2 py-0.5 text-[10px] uppercase tracking-wider"
                            title={!l.has_custom_salt ? "This URL has never been rotated — consider a rotation" : `Last rotated ${days} days ago`}
                          >
                            Consider rotating
                          </span>
                        );
                      })()}
                    </div>
                    {l.rotated_at && (
                      <div
                        data-testid={`airbnb-link-rotated-meta-${l.property_id}`}
                        className="mt-1 text-[11px] text-slate-400"
                      >
                        {timeAgo(l.rotated_at)}
                        {l.rotated_by ? <> · <span className="text-slate-500">{l.rotated_by}</span></> : null}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-2 align-top">
                    <Input
                      data-testid={`airbnb-link-input-${l.property_id}`}
                      readOnly
                      value={l.webhook_url}
                      className="h-9 bg-stone-50 font-mono text-[11px]"
                    />
                  </td>
                  <td className="px-2 py-2 text-right align-top">
                    <div className="inline-flex gap-1.5">
                      <Button
                        data-testid={`airbnb-link-copy-${l.property_id}`}
                        type="button"
                        variant="outline"
                        size="sm"
                        className="rounded-full h-9 px-4"
                        onClick={() => copy(l)}
                      >
                        <Copy className="w-3.5 h-3.5 mr-1.5" />
                        {copiedId === l.property_id ? "Copied" : "Copy"}
                      </Button>
                      <Button
                        data-testid={`airbnb-link-rotate-${l.property_id}`}
                        type="button"
                        variant="outline"
                        size="sm"
                        className="rounded-full h-9 px-4 text-amber-700 border-amber-200 hover:bg-amber-50"
                        onClick={() => rotate(l)}
                        disabled={rotatingId === l.property_id || rotatingAll}
                        title="Invalidate the old URL and mint a fresh one (use if the URL has leaked)"
                      >
                        <RotateCw className={`w-3.5 h-3.5 mr-1.5 ${rotatingId === l.property_id ? "animate-spin" : ""}`} />
                        {rotatingId === l.property_id ? "Rotating" : "Rotate"}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="mt-6 rounded-lg border border-slate-200 bg-stone-50 p-4 text-xs text-slate-500 leading-relaxed">
        <div className="ns-label text-slate-600 mb-1">Zapier recipe</div>
        <ol className="list-decimal pl-4 space-y-1">
          <li>Gmail → New Email trigger, filter: <code>from:@airbnb.com</code>.</li>
          <li>Webhooks by Zapier → POST → URL: <em>paste the row above for the matching listing</em>.</li>
          <li>Payload type: JSON. Body: <code>{"{ from: {{From Email}}, subject: {{Subject}}, body: {{Body Plain}}, sent_at: {{Date}} }"}</code>.</li>
        </ol>
      </div>
      <Dialog open={!!historyFor} onOpenChange={(v) => !v && setHistoryFor(null)}>
        <DialogContent
          data-testid="airbnb-rotation-history-modal"
          className="max-w-2xl max-h-[85vh] flex flex-col p-0 gap-0"
        >
          <DialogHeader className="px-6 py-4 border-b border-slate-200 shrink-0">
            <DialogTitle className="font-serif text-xl text-slate-900">
              Rotation history · {historyFor?.property_name}
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500 mt-1">
              Every rotation of this Airbnb signed URL, newest first.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto p-6">
            {historyLoading && (
              <div className="text-sm text-slate-400 italic">Loading history…</div>
            )}
            {!historyLoading && history && history.length === 0 && (
              <div className="text-sm text-slate-400 italic">No rotations recorded yet.</div>
            )}
            {!historyLoading && history && history.length > 0 && (
              <ol data-testid="airbnb-rotation-history-list" className="relative border-l border-slate-200 ml-3 space-y-6">
                {history.map((r) => {
                  const reasonBadge = {
                    manual: { color: "bg-slate-100 text-slate-700", label: "Manual" },
                    bulk: { color: "bg-teal-50 text-teal-700", label: "Bulk rotate" },
                    "team-offboard": { color: "bg-amber-50 text-amber-700", label: "Team off-boarding" },
                    "suspicious-login": { color: "bg-rose-50 text-rose-700", label: "Suspicious login" },
                  }[r.reason] || { color: "bg-slate-100 text-slate-600", label: r.reason || "unknown" };
                  return (
                    <li key={r.id} className="ml-6">
                      <span className="absolute -left-1.5 w-3 h-3 rounded-full bg-teal-600 border-2 border-white" />
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider ${reasonBadge.color}`}
                        >
                          {reasonBadge.label}
                        </span>
                        <span className="text-[11px] text-slate-400 font-mono">
                          {new Date(r.rotated_at).toLocaleString()}
                        </span>
                      </div>
                      <div className="mt-1 text-sm text-slate-700">
                        by <span className="font-mono text-slate-900">{r.rotated_by}</span>
                      </div>
                      {r.offboarded_email && (
                        <div className="text-xs text-slate-400 mt-0.5">
                          Triggered by removal of <span className="font-mono">{r.offboarded_email}</span>
                        </div>
                      )}
                      {r.suspicious_ip && (
                        <div className="text-xs text-slate-400 mt-0.5">
                          Blocked IP: <span className="font-mono">{r.suspicious_ip}</span>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
          <div className="px-6 py-4 border-t border-slate-200 shrink-0 flex justify-end">
            <Button
              data-testid="airbnb-rotation-history-close"
              variant="outline"
              className="rounded-full h-10 px-5"
              onClick={() => setHistoryFor(null)}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function GuidebookOrderCard() {
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings/guidebook-order")
      .then((r) => setOrder(r.data.sections))
      .catch(() => {});
  }, []);

  if (!order) return null;

  const LABELS = {
    door_code: "Door code",
    wifi: "Wi-Fi",
    checkin_instructions: "How to check in",
    guestbook_recommendations: "Local recommendations",
    guestbook_house_rules: "House rules",
    guestbook_checkout: "Check-out instructions",
    guestbook_emergency_contact: "Emergency contact",
    upsells_available: "Add-ons offered",
  };

  const move = (idx, dir) => {
    const next = [...order];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setOrder(next);
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings/guidebook-order", { sections: order });
      setOrder(data.sections);
      toast.success("Guidebook order saved");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="settings-guidebook-order" className="ns-card p-8">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-indigo-50 text-indigo-700 flex items-center justify-center">
          <BarChart3 className="w-5 h-5" />
        </div>
        <div>
          <div className="ns-label">Guest portal</div>
          <div className="font-serif text-2xl">Guidebook section order</div>
        </div>
      </div>
      <div className="ns-hairline my-6" />
      <p className="text-sm text-slate-500 max-w-xl mb-4">
        Reorder the sections guests see after check-in. Put your most-opened sections up top —
        the Reports page shows which ones guests hunt for most.
      </p>
      <div className="border border-slate-100 rounded-xl overflow-hidden max-w-xl">
        {order.map((key, i) => (
          <div
            key={key}
            data-testid={`guidebook-order-row-${key}`}
            className="flex items-center justify-between px-4 py-3 border-t border-slate-100 first:border-t-0 bg-white"
          >
            <div className="flex items-center gap-3">
              <span className="text-[11px] tabular-nums text-slate-400 w-5">{i + 1}</span>
              <span className="text-sm font-medium text-slate-800">{LABELS[key] || key}</span>
            </div>
            <div className="flex items-center gap-1">
              <button
                data-testid={`guidebook-order-up-${key}`}
                onClick={() => move(i, -1)}
                disabled={i === 0}
                className="w-8 h-8 rounded-full text-slate-500 hover:text-teal-700 hover:bg-teal-50 disabled:opacity-30 disabled:hover:bg-transparent"
                title="Move up"
              >
                ↑
              </button>
              <button
                data-testid={`guidebook-order-down-${key}`}
                onClick={() => move(i, 1)}
                disabled={i === order.length - 1}
                className="w-8 h-8 rounded-full text-slate-500 hover:text-teal-700 hover:bg-teal-50 disabled:opacity-30 disabled:hover:bg-transparent"
                title="Move down"
              >
                ↓
              </button>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-5">
        <Button
          data-testid="guidebook-order-save"
          onClick={save}
          disabled={busy}
          className="rounded-full ns-btn-primary h-11 px-6"
        >
          {busy ? "Saving…" : "Save order"}
        </Button>
      </div>
    </div>
  );
}

function CommissionsCard() {
  const [rates, setRates] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings/commissions").then((r) => setRates(r.data.rates)).catch(() => {});
  }, []);

  if (!rates) return null;

  const set = (k, v) => setRates({ ...rates, [k]: v === "" ? 0 : Number(v) });

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings/commissions", rates);
      setRates(data.rates);
      toast.success("Commission rates saved");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const CHANNELS = [
    { key: "airbnb", label: "Airbnb" },
    { key: "booking", label: "Booking.com" },
    { key: "vrbo", label: "Vrbo" },
    { key: "expedia", label: "Expedia" },
    { key: "internal", label: "Direct" },
    { key: "hostaway", label: "Hostaway" },
  ];

  return (
    <div data-testid="settings-commissions" className="ns-card p-8">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-teal-50 text-teal-700 flex items-center justify-center">
          <BarChart3 className="w-5 h-5" />
        </div>
        <div>
          <div className="ns-label">Reporting</div>
          <div className="font-serif text-2xl">Channel commission rates</div>
        </div>
      </div>
      <div className="ns-hairline my-6" />
      <p className="text-sm text-slate-500 max-w-xl">
        Used by the Owner Payout Report to calculate what each channel takes before the net payout.
      </p>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-5 max-w-2xl">
        {CHANNELS.map((c) => (
          <div key={c.key}>
            <Label className="text-xs text-slate-500">{c.label} %</Label>
            <Input
              data-testid={`settings-commission-${c.key}`}
              type="number"
              step="0.1"
              min="0"
              max="100"
              className="mt-1 h-10 bg-white"
              value={rates[c.key] ?? 0}
              onChange={(e) => set(c.key, e.target.value)}
            />
          </div>
        ))}
      </div>
      <Button
        data-testid="settings-commissions-save"
        onClick={save}
        disabled={busy}
        className="mt-6 rounded-full ns-btn-primary px-6"
      >
        {busy ? "Saving…" : "Save rates"}
      </Button>
    </div>
  );
}

function StarterPackCard() {
  const [enabled, setEnabled] = useState(true);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/settings/starter-pack/history");
      setHistory(data?.items || []);
    } catch { setHistory([]); }
  };

  useEffect(() => {
    api.get("/settings/starter-pack").then((r) => setEnabled(r.data.enabled !== false));
    loadHistory();
  }, []);

  const toggle = async (next) => {
    setBusy(true);
    try {
      await api.put("/settings/starter-pack", { enabled: next });
      setEnabled(next);
      toast.success(next ? "Starter pack enabled" : "Starter pack disabled — new properties ship clean");
      loadHistory();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ns-card p-8" data-testid="settings-starter-pack-card">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-indigo-50 text-indigo-700 flex items-center justify-center">
          <Home className="w-5 h-5" />
        </div>
        <div>
          <div className="ns-label">Operations</div>
          <div className="font-serif text-2xl">Bedding starter pack</div>
        </div>
      </div>
      <div className="ns-hairline my-6" />
      <p className="text-sm text-slate-500 max-w-xl">
        When a new property is created, Newport Stays seeds its bedding config with the
        portfolio-median bed layout so Operations stops typing zeros. Each seeded row carries
        an <b>auto · review</b> chip until a human confirms via Save. Disable to ship clean slates instead.
      </p>
      <label className="flex items-center gap-3 text-sm text-slate-700 select-none cursor-pointer mt-4">
        <input
          data-testid="settings-starter-pack-toggle"
          type="checkbox"
          checked={enabled}
          disabled={busy}
          onChange={(e) => toggle(e.target.checked)}
          className="w-4 h-4 accent-teal-600"
        />
        <span>Auto-seed new properties with the portfolio-median bedding config</span>
      </label>
      <div className="mt-4">
        <button
          type="button"
          onClick={() => setShowHistory((v) => !v)}
          data-testid="settings-starter-pack-history-toggle"
          className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-teal-700 underline"
        >
          {showHistory ? "Hide" : "Show"} toggle history ({history.length})
        </button>
        {showHistory && (
          <div
            data-testid="settings-starter-pack-history-list"
            className="mt-3 bg-stone-50 border border-slate-100 rounded-lg p-3 max-h-48 overflow-y-auto text-xs"
          >
            {history.length === 0 && (
              <div className="text-slate-400 italic text-center py-4">
                No toggle flips recorded yet.
              </div>
            )}
            {history.map((h, i) => (
              <div
                key={h.id || i}
                data-testid={`settings-starter-pack-history-row-${i}`}
                className="flex items-center justify-between gap-2 py-1 border-b border-slate-100 last:border-0"
              >
                <div className="min-w-0">
                  <span className="font-medium text-slate-700">{h.actor_name || "unknown"}</span>
                  <span className="text-slate-500">
                    {" "}· {h.from_enabled ? "on" : "off"} → {h.to_enabled ? "on" : "off"}
                  </span>
                </div>
                <span className="text-slate-400 text-[10px] whitespace-nowrap shrink-0">
                  {new Date(h.at).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" })}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DigestPreviewCard() {
  const [queue, setQueue] = useState([]);
  const [busy, setBusy] = useState(false);
  const [preferredHour, setPreferredHour] = useState(8);
  const [preferredTz, setPreferredTz] = useState("UTC");
  const [cronHour, setCronHour] = useState(8);
  const [hourBusy, setHourBusy] = useState(false);

  const loadQueue = async () => {
    try {
      const { data } = await api.get("/bedding/rate-change-queue");
      setQueue((data?.items || []).filter((r) => !r.dispatched));
    } catch { setQueue([]); }
  };
  const loadTiming = async () => {
    try {
      const { data } = await api.get("/settings/digest-timing");
      setPreferredHour(data.hour ?? 8);
      setPreferredTz(data.tz ?? "UTC");
      setCronHour(data.cron_hour_utc ?? 8);
    } catch { /* keep defaults */ }
  };
  useEffect(() => { loadQueue(); loadTiming(); }, []);

  const dispatchNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/bedding/dispatch-rate-digest");
      if (data?.events) {
        toast.success(`Digest sent · ${data.lines} change${data.lines === 1 ? "" : "s"} to ${data.dispatched} owner${data.dispatched === 1 ? "" : "s"}`);
      } else {
        toast.success("Queue is empty — nothing to send");
      }
      loadQueue();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const saveHour = async (h) => {
    setHourBusy(true);
    try {
      await api.put("/settings/digest-timing", { hour: Number(h), tz: preferredTz });
      setPreferredHour(Number(h));
      toast.success(`Digest will fire at ${String(h).padStart(2, "0")}:00 ${preferredTz}`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setHourBusy(false);
    }
  };

  const saveTz = async (tz) => {
    setHourBusy(true);
    try {
      await api.put("/settings/digest-timing", { hour: Number(preferredHour), tz });
      setPreferredTz(tz);
      toast.success(`Digest timezone set to ${tz}`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setHourBusy(false);
    }
  };

  // Common timezones Operations might want. Browser-provided IANA pool
  // is huge; curate to the ones that cover Newport Stays's landlords.
  const TZS = [
    "UTC", "Europe/London", "Europe/Dublin", "Europe/Paris", "Europe/Madrid",
    "Europe/Berlin", "Europe/Amsterdam", "Europe/Rome", "Europe/Zurich",
    "America/New_York", "America/Toronto", "America/Chicago", "America/Denver",
    "America/Los_Angeles", "Asia/Dubai", "Asia/Kolkata", "Asia/Singapore",
    "Asia/Hong_Kong", "Asia/Tokyo", "Australia/Sydney", "Pacific/Auckland",
  ];

  const willDefer = Number(preferredHour) > Number(cronHour);

  return (
    <div className="ns-card p-8" data-testid="settings-digest-preview-card">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-teal-50 text-teal-700 flex items-center justify-center">
          <Send className="w-5 h-5" />
        </div>
        <div>
          <div className="ns-label">Automations</div>
          <div className="font-serif text-2xl">Rate-change digest preview</div>
        </div>
      </div>
      <div className="ns-hairline my-6" />
      <p className="text-sm text-slate-500 max-w-xl">
        Rate card edits are batched into a single daily email instead of nagging Finance per save.
        Preview the queue below, or fire the digest now to see exactly what the daily-morning cron
        will send tomorrow.
      </p>

      <div className="mt-5 bg-stone-50 border border-slate-100 rounded-xl p-4" data-testid="settings-digest-timing-row">
        <div className="flex items-center gap-3 flex-wrap">
          <CalendarClock className="w-4 h-4 text-slate-500 shrink-0" />
          <label className="text-sm text-slate-700" htmlFor="settings-digest-hour">
            Preferred fire hour
          </label>
          <select
            id="settings-digest-hour"
            data-testid="settings-digest-hour-select"
            value={preferredHour}
            disabled={hourBusy}
            onChange={(e) => saveHour(e.target.value)}
            className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm tabular-nums"
          >
            {Array.from({ length: 24 }, (_, i) => (
              <option key={i} value={i}>{String(i).padStart(2, "0")}:00</option>
            ))}
          </select>
          <label className="text-sm text-slate-700" htmlFor="settings-digest-tz">
            in
          </label>
          <select
            id="settings-digest-tz"
            data-testid="settings-digest-tz-select"
            value={preferredTz}
            disabled={hourBusy}
            onChange={(e) => saveTz(e.target.value)}
            className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm"
          >
            {TZS.map((z) => <option key={z} value={z}>{z}</option>)}
          </select>
          <span className="text-[11px] text-slate-500">
            Cron slot fires at {String(cronHour).padStart(2, "0")}:00 UTC.
          </span>
        </div>
        <div
          data-testid="settings-digest-hour-help"
          className={`text-[11px] mt-2 ${willDefer ? "text-amber-700" : "text-slate-500"}`}
        >
          {willDefer
            ? `Preferred hour in ${preferredTz} currently sits after the cron slot — the daily run will skip and the digest will stay queued until you tap "Email me the digest now" or lower the preferred hour.`
            : `Daily run will fire on schedule — ${String(preferredHour).padStart(2, "0")}:00 ${preferredTz} translates to inside the cron window.`}
        </div>
      </div>

      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <div
          data-testid="settings-digest-queue-count"
          className="inline-flex items-center gap-2 rounded-full bg-stone-50 border border-slate-200 px-3 py-1 text-xs text-slate-600"
        >
          {queue.length} change{queue.length === 1 ? "" : "s"} pending
        </div>
        <Button
          type="button"
          onClick={dispatchNow}
          disabled={busy}
          data-testid="settings-digest-preview-btn"
          className="rounded-full ns-btn-primary h-9 px-5 gap-2"
        >
          <Send className="w-3 h-3" />
          {busy ? "Dispatching…" : "Email me the digest now"}
        </Button>
      </div>
      {queue.length > 0 && (
        <div className="mt-4 bg-stone-50 border border-slate-100 rounded-lg p-3 max-h-40 overflow-y-auto text-xs" data-testid="settings-digest-queue-list">
          {queue.slice(0, 6).map((r, i) => (
            <div key={i} className="flex items-start justify-between gap-2 py-1 border-b border-slate-100 last:border-0">
              <div className="min-w-0">
                <div className="font-medium text-slate-700 truncate">{r.actor_name || "unknown"}</div>
                <div className="text-slate-500 truncate">{(r.lines || []).join(" · ")}</div>
              </div>
              <div className="text-slate-400 text-[10px] shrink-0 whitespace-nowrap">
                {new Date(r.at).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" })}
              </div>
            </div>
          ))}
          {queue.length > 6 && (
            <div className="text-slate-400 text-[10px] text-center pt-2">
              … +{queue.length - 6} more
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function BirthdayCard() {
  const [enabled, setEnabled] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings/birthday").then((r) => setEnabled(r.data.enabled !== false));
  }, []);

  const toggle = async (next) => {
    setBusy(true);
    try {
      await api.put("/settings/birthday", { enabled: next });
      setEnabled(next);
      toast.success(next ? "Birthday emails enabled" : "Birthday emails paused");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ns-card p-8">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-teal-50 text-teal-700 flex items-center justify-center">
          <Cake className="w-5 h-5" />
        </div>
        <div>
          <div className="ns-label">Automations</div>
          <div className="font-serif text-2xl">Birthday greetings</div>
        </div>
      </div>
      <div className="ns-hairline my-6" />
      <p className="text-sm text-slate-500 max-w-xl">
        Newport Stays automatically sends a warm birthday email to every past guest at 08:30 UTC on their special day.
        Uses the &quot;Birthday greeting&quot; template — edit it any time from the Templates page.
      </p>
      <label className="flex items-center gap-3 text-sm text-slate-700 select-none cursor-pointer mt-4">
        <input
          data-testid="settings-birthday-toggle"
          type="checkbox"
          checked={enabled}
          disabled={busy}
          onChange={(e) => toggle(e.target.checked)}
          className="w-4 h-4 accent-teal-600"
        />
        <span>Send birthday greetings automatically</span>
      </label>
    </div>
  );
}

function StatementNoteCard() {
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings/statement-note").then((r) => setMsg(r.data.message || "")).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings/statement-note", { message: msg });
      toast.success("Landlord note saved");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ns-card p-8">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-teal-50 text-teal-700 flex items-center justify-center">
          <MessageSquare className="w-5 h-5" />
        </div>
        <div>
          <div className="ns-label">Monthly payout email</div>
          <div className="font-serif text-2xl">Message from Newport Stays</div>
        </div>
      </div>
      <div className="ns-hairline my-6" />
      <p className="text-sm text-slate-500 max-w-xl mb-4">
        A short note appended to every landlord payout email (both the auto monthly one on the 1st
        and any one-off statements you send from the Reports page).
      </p>
      <textarea
        data-testid="settings-statement-note"
        rows={4}
        value={msg}
        onChange={(e) => setMsg(e.target.value)}
        placeholder="Thanks for another wonderful month with Newport Stays. A gentle reminder that summer bookings are pouring in — let me know if you'd like to review your rates."
        className="w-full max-w-xl rounded-md border border-slate-200 bg-white p-4 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-teal-600/30"
      />
      <div className="mt-4">
        <Button
          data-testid="settings-statement-note-save"
          onClick={save}
          disabled={busy}
          className="rounded-full ns-btn-primary h-11 px-6"
        >
          {busy ? "Saving…" : "Save note"}
        </Button>
      </div>
    </div>
  );
}

export function PortalBrandingCard() {
  const [form, setForm] = useState({
    brand_name: "Newport Stays",
    brand_tagline: "Owner Portal",
    accent_color: "#0d7a75",
    powered_by_visible: true,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings/portal-brand").then((r) => setForm(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings/portal-brand", form);
      setForm({
        brand_name: data.brand_name,
        brand_tagline: data.brand_tagline,
        accent_color: data.accent_color,
        powered_by_visible: data.powered_by_visible,
      });
      toast.success("Portal branding saved");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const isCustom = form.brand_name && form.brand_name !== "Newport Stays";

  return (
    <div className="ns-card p-8">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-teal-50 text-teal-700 flex items-center justify-center">
          <Palette className="w-5 h-5" />
        </div>
        <div>
          <div className="ns-label">Landlord portal</div>
          <div className="font-serif text-2xl">Portal branding</div>
        </div>
      </div>
      <div className="ns-hairline my-6" />
      <p className="text-sm text-slate-500 max-w-xl mb-6">
        Present the landlord portal under your own brand. This changes the header, footer copy,
        and email tagline that your landlords see when signing in.
      </p>
      <div className="grid gap-4 max-w-md">
        <div>
          <Label className="ns-label">Brand name</Label>
          <Input
            data-testid="settings-brand-name"
            className="mt-2 h-11 bg-white"
            value={form.brand_name}
            onChange={(e) => setForm({ ...form, brand_name: e.target.value })}
            placeholder="Newport Stays"
          />
        </div>
        <div>
          <Label className="ns-label">Tagline</Label>
          <Input
            data-testid="settings-brand-tagline"
            className="mt-2 h-11 bg-white"
            value={form.brand_tagline}
            onChange={(e) => setForm({ ...form, brand_tagline: e.target.value })}
            placeholder="Owner Portal"
          />
        </div>
        <div>
          <Label className="ns-label">Accent colour</Label>
          <div className="flex items-center gap-3 mt-2">
            <input
              data-testid="settings-brand-color"
              type="color"
              value={form.accent_color}
              onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
              className="h-11 w-16 rounded-md border border-slate-200 bg-white cursor-pointer"
            />
            <code className="text-xs text-slate-500 font-mono">{form.accent_color}</code>
          </div>
        </div>
        <label className="flex items-start gap-3 text-sm text-slate-700 select-none cursor-pointer mt-2">
          <input
            data-testid="settings-brand-powered-by"
            type="checkbox"
            checked={!!form.powered_by_visible}
            onChange={(e) => setForm({ ...form, powered_by_visible: e.target.checked })}
            className="w-4 h-4 accent-teal-600 mt-0.5"
          />
          <span>
            Show &ldquo;Powered by Newport Stays&rdquo;
            <span className="block text-[11px] text-slate-400 mt-1">
              Only appears when the brand name is customised. {isCustom ? "Currently visible on the portal footer." : "You're using the default Newport Stays branding — the badge is hidden either way."}
            </span>
          </span>
        </label>
        <div className="pt-2">
          <Button
            data-testid="settings-brand-save"
            onClick={save}
            disabled={busy}
            className="rounded-full ns-btn-primary h-11 px-6"
          >
            {busy ? "Saving…" : "Save branding"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function UpsellsCard() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null); // {mode:'new'|'edit', form}
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/upsells").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const open = (mode, u = null) => setEditing({
    mode,
    id: u?.id,
    form: {
      name: u?.name || "",
      description: u?.description || "",
      price: u?.price || 0,
      is_active: u ? !!u.is_active : true,
    },
  });

  const save = async () => {
    const { name, description, price, is_active } = editing.form;
    if (!name?.trim()) { toast.error("Name is required"); return; }
    if (isNaN(Number(price)) || Number(price) < 0) { toast.error("Price must be a positive number"); return; }
    setBusy(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim(),
        price: Number(price),
        is_active,
      };
      if (editing.mode === "edit") {
        await api.put(`/upsells/${editing.id}`, payload);
        toast.success("Upsell updated");
      } else {
        await api.post("/upsells", payload);
        toast.success("Upsell added");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete "${u.name}"?`)) return;
    try {
      await api.delete(`/upsells/${u.id}`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const toggle = async (u) => {
    try {
      await api.put(`/upsells/${u.id}`, {
        name: u.name, description: u.description || "", price: u.price, is_active: !u.is_active,
      });
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="ns-card p-8">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-teal-50 text-teal-700 flex items-center justify-center">
          <Sparkles className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className="ns-label">Guest journey</div>
          <div className="font-serif text-2xl">Upsells & extras</div>
        </div>
        <Button
          data-testid="settings-upsell-add"
          onClick={() => open("new")}
          className="rounded-full ns-btn-primary h-11 px-5 text-xs"
        >
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Add extra
        </Button>
      </div>
      <div className="ns-hairline my-6" />
      <p className="text-sm text-slate-500 max-w-xl mb-4">
        Anything you can charge for during the pre-arrival journey — early check-in, welcome hampers,
        mid-stay cleans. These appear as optional add-ons on every guest check-in link.
      </p>
      {rows.length === 0 ? (
        <div className="text-center py-8 text-slate-400 text-sm italic">
          No extras yet. Add your first upsell to start earning per stay.
        </div>
      ) : (
        <div className="divide-y divide-slate-100 border border-slate-100 rounded-xl overflow-hidden">
          {rows.map((u) => (
            <div key={u.id} data-testid={`settings-upsell-${u.id}`} className="p-4 flex items-center gap-4 hover:bg-stone-50">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-medium text-sm">{u.name}</div>
                  {!u.is_active && (
                    <span className="text-[10px] tracking-widest uppercase text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">Hidden</span>
                  )}
                </div>
                {u.description && <div className="text-xs text-slate-500 mt-1 truncate">{u.description}</div>}
              </div>
              <div className="font-serif text-lg">£{Number(u.price).toFixed(2)}</div>
              <button
                onClick={() => toggle(u)}
                className="text-xs text-slate-500 hover:text-teal-700 px-2"
                title={u.is_active ? "Hide from guest journey" : "Show on guest journey"}
              >
                {u.is_active ? "Hide" : "Show"}
              </button>
              <button
                onClick={() => open("edit", u)}
                className="text-slate-300 hover:text-teal-700 p-1"
                title="Edit"
              >
                <Pencil className="w-4 h-4" />
              </button>
              <button
                onClick={() => remove(u)}
                className="text-slate-300 hover:text-red-600 p-1"
                title="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="font-serif text-2xl mb-4">{editing.mode === "edit" ? "Edit extra" : "Add extra"}</div>
            <div className="space-y-4 text-sm">
              <div>
                <Label className="ns-label">Name</Label>
                <Input
                  data-testid="settings-upsell-name"
                  className="mt-2 h-11 bg-white"
                  value={editing.form.name}
                  onChange={(e) => setEditing({ ...editing, form: { ...editing.form, name: e.target.value } })}
                  placeholder="Early check-in"
                />
              </div>
              <div>
                <Label className="ns-label">Description</Label>
                <Input
                  className="mt-2 h-11 bg-white"
                  value={editing.form.description}
                  onChange={(e) => setEditing({ ...editing, form: { ...editing.form, description: e.target.value } })}
                  placeholder="Get in from 12pm instead of 4pm"
                />
              </div>
              <div>
                <Label className="ns-label">Price (£)</Label>
                <Input
                  data-testid="settings-upsell-price"
                  type="number"
                  step="0.01"
                  min={0}
                  className="mt-2 h-11 bg-white"
                  value={editing.form.price}
                  onChange={(e) => setEditing({ ...editing, form: { ...editing.form, price: e.target.value } })}
                />
              </div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editing.form.is_active}
                  onChange={(e) => setEditing({ ...editing, form: { ...editing.form, is_active: e.target.checked } })}
                />
                Visible on every guest check-in
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
              <Button data-testid="settings-upsell-save" onClick={save} disabled={busy} className="rounded-full ns-btn-primary px-6">
                {busy ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function SettingsPage() {
  const { user, setUser } = useAuth();

  // Profile / email preferences
  const [replyTo, setReplyTo] = useState("");
  const [name, setName] = useState("");
  const [receivesDigest, setReceivesDigest] = useState(true);
  const [muteRateLimitDigest, setMuteRateLimitDigest] = useState(false);
  const [muteOutlierDigest, setMuteOutlierDigest] = useState(false);
  const [muteWeeklyOpsDigest, setMuteWeeklyOpsDigest] = useState(false);
  const [profileBusy, setProfileBusy] = useState(false);

  useEffect(() => {
    if (user) {
      setReplyTo(user.reply_to_email || "");
      setName(user.name || "");
      setReceivesDigest(user.receives_digest !== false);
      setMuteRateLimitDigest(user.mute_rate_limit_digest === true);
      setMuteOutlierDigest(user.mute_outlier_digest === true);
      setMuteWeeklyOpsDigest(user.mute_weekly_ops_digest === true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email, user?.reply_to_email, user?.name, user?.receives_digest, user?.mute_rate_limit_digest, user?.mute_outlier_digest, user?.mute_weekly_ops_digest]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setProfileBusy(true);
    try {
      const { data } = await api.patch("/auth/profile", {
        reply_to_email: replyTo,
        name,
        receives_digest: receivesDigest,
        mute_rate_limit_digest: muteRateLimitDigest,
        mute_outlier_digest: muteOutlierDigest,
        mute_weekly_ops_digest: muteWeeklyOpsDigest,
      });
      setUser(data);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setProfileBusy(false);
    }
  };

  // Password change
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const savePassword = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (next !== confirm) {
      toast.error("New password and confirmation do not match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password updated");
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout
      title="Settings"
      subtitle="Your Newport Stays account, sign-in details and email preferences."
    >
      <div data-testid={SETTINGS.root} className="grid lg:grid-cols-[340px_1fr] gap-6">
        <div className="ns-card p-6 h-max">
          <div className="ns-label">Signed in as</div>
          <div className="font-serif text-xl mt-1">{user?.name}</div>
          <div className="text-sm text-slate-500">{user?.email}</div>
          {user?.role && (
            <div className="mt-3">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs bg-teal-50 text-teal-800 border border-teal-100 capitalize">
                {user.role}
              </span>
            </div>
          )}
          <div className="ns-hairline my-4" />
          <div className="flex items-start gap-3 text-xs text-slate-500 leading-relaxed">
            <ShieldCheck className="w-4 h-4 text-teal-700 mt-0.5" />
            <span>
              Passwords are hashed with bcrypt. Once you save, your old password
              stops working immediately — you&apos;ll stay signed in on this device.
            </span>
          </div>
        </div>

        <div className="space-y-6">
          <form onSubmit={saveProfile} className="ns-card p-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-teal-50 text-teal-700 flex items-center justify-center">
                <MailPlus className="w-5 h-5" />
              </div>
              <div>
                <div className="ns-label">Email preferences</div>
                <div className="font-serif text-2xl">Reply-to for campaigns</div>
              </div>
            </div>

            <div className="ns-hairline my-6" />

            <div className="grid gap-4 max-w-md">
              <div>
                <Label className="ns-label">Display name</Label>
                <Input
                  className="mt-2 h-11 bg-white"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <div className="text-[11px] text-slate-400 mt-1.5">
                  Shown on the sidebar and in your invitation emails.
                </div>
              </div>
              <div>
                <Label className="ns-label">Preferred reply-to email</Label>
                <Input
                  type="email"
                  className="mt-2 h-11 bg-white"
                  placeholder="hello@newportstays.co.uk"
                  value={replyTo}
                  onChange={(e) => setReplyTo(e.target.value)}
                />
                <div className="text-[11px] text-slate-400 mt-1.5">
                  Every mail-shot you create will use this address as the
                  reply-to. Leave blank to fall back to your login email
                  (<span className="text-slate-500">{user?.email}</span>).
                </div>
              </div>
              <div className="pt-2">
                <label className="flex items-center gap-3 text-sm text-slate-700 select-none cursor-pointer mb-3">
                  <input
                    type="checkbox"
                    checked={receivesDigest}
                    onChange={(e) => setReceivesDigest(e.target.checked)}
                    className="w-4 h-4 accent-teal-600"
                  />
                  <span>
                    Send me the morning digest email
                    <span className="block text-[11px] text-slate-400">
                      Owners only — untick if you&apos;re on holiday.
                    </span>
                  </span>
                </label>
                <label
                  data-testid="settings-mute-rate-limit-digest"
                  className="flex items-center gap-3 text-sm text-slate-700 select-none cursor-pointer mb-3"
                >
                  <input
                    type="checkbox"
                    checked={muteRateLimitDigest}
                    onChange={(e) => setMuteRateLimitDigest(e.target.checked)}
                    className="w-4 h-4 accent-teal-600"
                  />
                  <span>
                    Mute the weekly supplier-health digest
                    <span className="block text-[11px] text-slate-400">
                      Skip the Monday email about cleaning companies that hit the retry rate-limit.
                    </span>
                  </span>
                </label>
                <label
                  data-testid="settings-mute-outlier-digest"
                  className="flex items-center gap-3 text-sm text-slate-700 select-none cursor-pointer mb-3"
                >
                  <input
                    type="checkbox"
                    checked={muteOutlierDigest}
                    onChange={(e) => setMuteOutlierDigest(e.target.checked)}
                    className="w-4 h-4 accent-teal-600"
                  />
                  <span>
                    Mute the weekly cleaning-cost outlier digest
                    <span className="block text-[11px] text-slate-400">
                      Skip the Monday email listing properties charging more than 20% above the portfolio average.
                    </span>
                  </span>
                </label>
                <label
                  data-testid="settings-mute-weekly-ops-digest"
                  className="flex items-center gap-3 text-sm text-slate-700 select-none cursor-pointer mb-4"
                >
                  <input
                    type="checkbox"
                    checked={muteWeeklyOpsDigest}
                    onChange={(e) => setMuteWeeklyOpsDigest(e.target.checked)}
                    className="w-4 h-4 accent-teal-600"
                  />
                  <span>
                    Mute the Monday weekly operations email
                    <span className="block text-[11px] text-slate-400">
                      Skip the summary of average time-on-site + geofence mismatches per property.
                    </span>
                  </span>
                </label>
                <Button
                  type="submit"
                  disabled={profileBusy}
                  className="rounded-full ns-btn-primary h-11 px-6"
                >
                  {profileBusy ? "Saving…" : "Save preferences"}
                </Button>
              </div>
            </div>
          </form>

          <form onSubmit={savePassword} className="ns-card p-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-teal-50 text-teal-700 flex items-center justify-center">
                <KeyRound className="w-5 h-5" />
              </div>
              <div>
                <div className="ns-label">Security</div>
                <div className="font-serif text-2xl">Change password</div>
              </div>
            </div>

            <div className="ns-hairline my-6" />

            <div className="grid gap-4 max-w-md">
              <div>
                <Label className="ns-label">Current password</Label>
                <Input
                  data-testid={SETTINGS.currentPasswordInput}
                  type="password"
                  className="mt-2 h-11 bg-white"
                  autoComplete="current-password"
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                  required
                />
              </div>
              <div>
                <Label className="ns-label">New password</Label>
                <Input
                  data-testid={SETTINGS.newPasswordInput}
                  type="password"
                  className="mt-2 h-11 bg-white"
                  autoComplete="new-password"
                  value={next}
                  onChange={(e) => setNext(e.target.value)}
                  minLength={8}
                  required
                />
                <div className="text-[11px] text-slate-400 mt-1.5">
                  At least 8 characters. Use a passphrase you can remember.
                </div>
              </div>
              <div>
                <Label className="ns-label">Confirm new password</Label>
                <Input
                  data-testid={SETTINGS.confirmPasswordInput}
                  type="password"
                  className="mt-2 h-11 bg-white"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                />
              </div>
              <div className="pt-2">
                <Button
                  data-testid={SETTINGS.savePasswordBtn}
                  type="submit"
                  disabled={busy || !current || !next || !confirm}
                  className="rounded-full ns-btn-primary h-11 px-6"
                >
                  {busy ? "Updating…" : "Save new password"}
                </Button>
              </div>
            </div>
          </form>

          {user?.role === "owner" && <BirthdayCard />}
          {user?.role === "owner" && <StarterPackCard />}
          {user?.role === "owner" && <DigestPreviewCard />}
          {user?.role === "owner" && <WeeklyDigestCard />}
          {(user?.role === "owner" || user?.role === "admin") && <CleaningCompaniesCard />}
          {(user?.role === "owner" || user?.role === "admin") && (
            <div className="mt-6">
              <RateLimitWeeklyCard />
            </div>
          )}
          {(user?.role === "owner" || user?.role === "admin") && <PendingWhatsAppCard />}
          <UiPrefsCard />
          <SectionThemePickerCard />
          <SharedOutlookCalendarsCard />
          <TwoFactorCard />
          <BackupCodesCard />
          {user?.role === "owner" && <CommissionsCard />}
          {user?.role === "owner" && <AiDraftLimitsCard />}
          {user?.role === "owner" && <OtaBridgeCard />}
          {(user?.role === "owner" || user?.role === "admin") && <AirbnbLinksCard />}
          {(user?.role === "owner" || user?.role === "admin") && <RequiredGuidesCard />}
          {(user?.role === "owner" || user?.role === "admin") && <QuizEditorCard />}
          {(user?.role === "owner" || user?.role === "admin") && <CheckoutPhotoSettingCard />}
          <RestartTourCard />
          {(user?.role === "owner" || user?.role === "admin") && <DepositBoostCard />}
          {(user?.role === "owner" || user?.role === "admin") && <CareTipsCard />}
          {user?.role === "owner" && <StatementNoteCard />}
          {user?.role === "owner" && <ChannelManagerCard />}
          {user?.role === "owner" && <SenderDomainCard />}
          {user?.role === "owner" && <HostawayCard />}
          {user?.role === "owner" && <TwilioCard />}
          {user?.role === "owner" && <SlackCard />}
        </div>
      </div>
    </Layout>
  );
}
